import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type Active,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Icon, InputField } from '@saviynt/design-system';

import { moveArrayItem } from '../../utils';

import {
  ViolationsWidgetMenuItemDraggable,
  type ViolationsWidgetMenuItemOption,
  ViolationsWidgetMenuItemPlaceholder,
} from './ViolationsWidgetMenuItem';

import './ViolationsWidgetMenuList.css';

const BASE_ITEM_HEIGHT = 74;
const MIN_COUNT_OPTS_FOR_SEARCH = 6;

interface ViolationsWidgetMenuListProps {
  options: ViolationsWidgetMenuItemOption[];
  isDisabled: boolean;
  handleChange: (optionsList: ViolationsWidgetMenuItemOption[]) => void;
}

/**
 * ViolationsWidgetMenuList
 */
function ViolationsWidgetMenuList({
  options,
  isDisabled,
  handleChange,
}: ViolationsWidgetMenuListProps): ReactElement {
  const dropDownBodyRef = useRef<HTMLDivElement | null>(null);

  // Type of options
  const [searchText, setSearchText] = useState('');
  const [lastValidSearchText, setLastValidSearchText] = useState('');
  const [optionsList, setOptionsList] = useState<
    ViolationsWidgetMenuItemOption[]
  >([]);
  // Dragged item logic
  const [draggedItem, setDraggedItem] = useState<UniqueIdentifier | null>(null);
  const [draggingItemIndex, setDraggingItemIndex] = useState<number | null>(
    null
  );
  const [draggingItemScrollAmount, setDraggingItemScrollAmount] =
    useState<number>(0);

  useEffect(() => {
    if (options.length > 0) {
      setOptionsList(options);
    }
  }, [options]);

  // Constants
  const isSearchBarAvailable = useMemo(
    () => optionsList.length > MIN_COUNT_OPTS_FOR_SEARCH,
    [optionsList]
  );
  const filteredOptions = useMemo(() => {
    if (searchText === '') {
      setLastValidSearchText(searchText);

      return optionsList;
    }

    const modifiedOptionsList = optionsList.filter((option) =>
      option.label.toLowerCase().includes(searchText.toLowerCase())
    );

    if (modifiedOptionsList.length > 0) {
      setLastValidSearchText(searchText);

      return modifiedOptionsList;
    }

    return optionsList.filter((option) =>
      option.label.toLowerCase().includes(lastValidSearchText.toLowerCase())
    );
  }, [optionsList, searchText, lastValidSearchText]);
  const checkedOptions = useMemo(
    () => filteredOptions.filter((option) => option.isChecked),
    [filteredOptions]
  );
  const uncheckedOptions = useMemo(
    () => filteredOptions.filter((option) => !option.isChecked),
    [filteredOptions]
  );

  // Checkbox logic
  const handleCheckboxChange = useCallback(
    (optionValue: string, isChecked: boolean) => {
      const checkedOptionsCount = checkedOptions.length;

      // If current checked count is 2 and going to be 1 then disable the last one
      if (isChecked && checkedOptionsCount === 2) {
        const modifiedOptions = optionsList
          .map((option) =>
            option.value === optionValue
              ? { ...option, isChecked: !option.isChecked }
              : option
          )
          .sort((a, b) => Number(b.isChecked) - Number(a.isChecked))
          .map((option) => ({
            ...option,
            isDisabled: option.isChecked || option.isDisabled,
          }));

        setOptionsList(modifiedOptions);
        handleChange(modifiedOptions);

        return;
      }

      if (isChecked && checkedOptionsCount === 1) {
        return;
      }

      // If atleast current checked count is 1 and count is going to increase
      const modifiedOptions = optionsList
        .map((option) =>
          option.value === optionValue
            ? { ...option, isChecked: !option.isChecked }
            : {
                ...option,
                isDisabled: option.isChecked ? false : option.isDisabled,
              }
        )
        .sort((a, b) => Number(b.isChecked) - Number(a.isChecked));

      setOptionsList(modifiedOptions);
      handleChange(modifiedOptions);
    },
    [handleChange, optionsList, checkedOptions.length]
  );

  // Drap and drop logic
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 20,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = ({ active }: { active: Active }) => {
    const scrollAmount = dropDownBodyRef.current?.scrollTop ?? 0;

    const index = checkedOptions.findIndex((o) => o.value === active.id);

    setDraggedItem(active.id);
    setDraggingItemIndex(index);
    setDraggingItemScrollAmount(scrollAmount);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedItem(null);
    setDraggingItemIndex(null);
    setDraggingItemScrollAmount(0);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = checkedOptions.findIndex((o) => o.value === active.id);
    const newIndex = checkedOptions.findIndex((o) => o.value === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const newCheckedOptions = moveArrayItem(checkedOptions, oldIndex, newIndex);
    const mpdifiedOptionsList = [...newCheckedOptions, ...uncheckedOptions];

    setOptionsList(mpdifiedOptionsList);
    handleChange(mpdifiedOptionsList);
  };

  const draggedOption = useMemo(
    () =>
      draggedItem ? checkedOptions.find((o) => o.value === draggedItem) : null,
    [checkedOptions, draggedItem]
  );

  const overlayTopItemPosition =
    draggingItemIndex !== null
      ? draggingItemIndex * BASE_ITEM_HEIGHT - draggingItemScrollAmount
      : 0;

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: overlayTopItemPosition,
    left: 0,
    opacity: 1,
  };

  return (
    <>
      {isSearchBarAvailable && (
        <Box tag='div' className='Sod-ViolationsWidgetMenu-searchBox'>
          <InputField
            name='basic'
            placeholder='Search'
            size='large'
            value={searchText}
            setValue={setSearchText}
            prefixIcon={
              <Icon kind='Search' color='--color-foreground-neutral-subtle' />
            }
          />
        </Box>
      )}
      <div style={{ position: 'relative' }}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <div
            ref={dropDownBodyRef}
            className='Sod-ViolationsWidgetMenu-listDraggable'>
            <SortableContext
              items={checkedOptions.map((option) => option.value)}
              strategy={verticalListSortingStrategy}>
              {checkedOptions.map((option) => (
                <ViolationsWidgetMenuItemDraggable
                  key={option.value}
                  option={option}
                  handleClick={handleCheckboxChange}
                />
              ))}
            </SortableContext>
            <div className='Sod-ViolationsWidgetMenu-fixedList'>
              {uncheckedOptions
                .filter((option) => !option.isDisabled)
                // .sort((b, a) => Number(b.isDisabled) - Number(a.isDisabled))
                .map((option) => (
                  <ViolationsWidgetMenuItemPlaceholder
                    key={option.value}
                    option={{
                      ...option,
                      isDisabled: option.isDisabled || isDisabled,
                    }}
                    handleClick={handleCheckboxChange}
                  />
                ))}
            </div>
          </div>
          <DragOverlay style={overlayStyle}>
            {draggedOption ? (
              <ViolationsWidgetMenuItemPlaceholder
                option={draggedOption}
                handleClick={() => {}}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </>
  );
}

export default memo(ViolationsWidgetMenuList);
